import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { type Playlist, PlaylistApi, PlaylistCreateData, } from '@shared/interfacres/playlist';
import { inject } from '@angular/core';

import { firstValueFrom } from 'rxjs';

import type { GetFilter, GetPagination } from '@shared/interfacres/request-related';

import { PlaylistsManagerService } from '@data-access/manager/services/playlists-manager.service';
import { DEFAULT_PAGE_SIZE } from '@shared/constants/basic';
import { MatSnackBar } from '@angular/material/snack-bar';


interface PlaylistsState {
  items: Playlist[];
  fullList: Playlist[];
  total: number;
  name: string;
  isLoading: boolean;
  filter: GetFilter;
  pagination: GetPagination;
}

const initialState: PlaylistsState = {
  items: [],
  fullList: [],
  total: 0,
  name: '',
  isLoading: false,
  filter: { query: '', order: 'asc' },
  pagination: { page: 1, pageSize: DEFAULT_PAGE_SIZE }
};

export const PlaylistsManagerStore = signalStore(
  withState(initialState),

  withMethods((store, service = inject(PlaylistsManagerService), snackBar = inject(MatSnackBar)) => ({


    getByName(name: string) {
      patchState(store, () => ({
        name
      }));
      return this.getFiltered();
    },

    setPagination(page: number, pageSize: number) {
      patchState(store, (state) => ({
        pagination: {
          ...state.pagination,
          page,
          pageSize,
        }
      }));
      return this.getFiltered();
    },

    setFilter(query: string, order: 'asc' | 'desc') {
      patchState(store, (state) => ({
        filter: {
          ...state.filter,
          query,
          order,
        }
      }));
      return this.getFiltered();
    },

    async getFiltered() {
      patchState(store, () => ({
        isLoading: true,
      }));

      const name = store.name() || '';

      const withPagination = await firstValueFrom(service.getFiltered(name, store.filter(), store.pagination()));

      patchState(store, () => ({
        isLoading: false,
        items: withPagination.data.map((playlist: PlaylistApi) => {
          return {
            ...playlist,
          }
        }),
        total: withPagination.pagination.total
      }));

    },

    async getAll() {
      patchState(store, () => ({
        isLoading: true,
      }));

      const items = await firstValueFrom(service.getList());
      patchState(store, () => ({
        isLoading: false,
        fullList: items
      }));

    },
    getImageById(id: number) {
      return service.getImageById(id);
    },

    clearCriteria() {
      patchState(store, () => ({
        name: '',
        filter: { query: '', order: 'asc' } as GetFilter,
        pagination: { page: 1, pageSize: DEFAULT_PAGE_SIZE }
      }));
    },
    clearData() {
      this.clearCriteria();
      patchState(store, () => ({
        items: [],
        fullList: [],
        total: 0,
      }));
    },
    async create(data: PlaylistCreateData) {
      patchState(store, () => ({
        isLoading: true,
      }));

      try {
        await firstValueFrom(service.create(data));
        // this.clearCriteria();

        return this.getFiltered();

      } catch (e) {
        snackBar.open('Error during creation', '', { duration: 2000, horizontalPosition: 'end', panelClass: 'error-toast' });
        console.error(e);
      } finally {
        patchState(store, () => ({
          isLoading: false
        }));
      }

    },
    async updatePartial(id: number, payload: Partial<Playlist>) {
      try {
        await firstValueFrom(service.updatePartial(id, payload));
      } catch (e) {
        snackBar.open('Error during update', '', { duration: 2000, horizontalPosition: 'end', panelClass: 'error-toast' });
        console.error(e);
      }
    },
    async update(id: number, data: PlaylistCreateData) {
      patchState(store, () => ({
        isLoading: true,
      }));

      try {
        await firstValueFrom(service.update(id, data));
        // this.clearCriteria();

        return this.getFiltered();

      } catch (e) {
        snackBar.open('Error during update', '', { duration: 2000, horizontalPosition: 'end', panelClass: 'error-toast' });
        console.error(e);
      } finally {
        patchState(store, () => ({
          isLoading: false
        }));
      }
    },

    async setPlaylistsForTrack( trackId: number, playlistsIds: number[]) {
      patchState(store, () => ({
        isLoading: true,
      }));

      try {
        await firstValueFrom(service.addTracksToPlaylists(trackId, playlistsIds));
        // this.clearCriteria();

        snackBar.open('Success', 'Tracks were added to playlists', { duration: 2000, horizontalPosition: 'end', panelClass: '' });
        return this.getFiltered();

      } catch (e) {
        snackBar.open('Error during update', '', { duration: 2000, horizontalPosition: 'end', panelClass: 'error-toast' });
        console.error(e);
      } finally {
        patchState(store, () => ({
          isLoading: false
        }));
      }
    },

    async delete(ids: number[]) {
      patchState(store, () => ({
        isLoading: true,
      }));

      try {
        await firstValueFrom(service.delete(ids));
        // this.clearCriteria();

        return this.getFiltered();

      } catch (e) {
        snackBar.open('Error during removing', '', { duration: 2000, horizontalPosition: 'end', panelClass: 'error-toast' });
        console.error(e);
      } finally {
        patchState(store, () => ({
          isLoading: false
        }));
      }
    },
    async deleteTracksFromPlaylist(playListId: number, trackIds: number[]) {
      patchState(store, () => ({
        isLoading: true,
      }));
      try {
        await firstValueFrom(service.deleteTracksFromPlaylist(playListId, trackIds));
        await this.getFiltered();

      } catch (e) {
        snackBar.open('Error during removing', '', { duration: 2000, horizontalPosition: 'end', panelClass: 'error-toast' });
        console.error(e);
      } finally {
        patchState(store, () => ({
          isLoading: false
        }));
      }
    }

  }))
);
